/**
 * Bulk Selection Hook
 *
 * Provides state and actions for managing bulk selection in list views
 */
import { useState, useCallback, useMemo } from 'react'

export interface BulkSelectionHook<T> {
  selectedIds: Set<string>
  isSelected: (id: string) => boolean
  toggleSelection: (id: string) => void
  selectAll: (items: T[]) => void
  deselectAll: () => void
  selectedCount: number
  selectedItems: T[]
}

export function useBulkSelection<T extends { id: string }>(items: T[]): BulkSelectionHook<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  )

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback((allItems: T[]) => {
    setSelectedIds(new Set(allItems.map((item) => item.id)))
  }, [])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.id)),
    [items, selectedIds]
  )

  return {
    selectedIds,
    isSelected,
    toggleSelection,
    selectAll,
    deselectAll,
    selectedCount: selectedIds.size,
    selectedItems,
  }
}
